/*
 * See the file LICENSE for the full license governing this code.
 */

import * as React from 'react';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';
import Popover from '@mui/material/Popover';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import OutlinedInput from '@mui/material/OutlinedInput';
import StopIcon from '@mui/icons-material/Stop';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import _ from 'lodash';
import StateIcon from './StateIcon';
import Options from './Options';
import {
  useClusterMutation,
  networkWithErrorConvert,
  useQuery,
} from '../../api';
import CopyToClipboard from '../CopyToClipboard';

const queryCluster = (id) => ({
  method: 'get',
  url: '/v1/cluster',
  params: { id },
});

const useClusterStateModification = (state) =>
  useClusterMutation((id) => ({
    method: 'post',
    url: '/v1/cluster/state',
    data: { id, state },
  }));

function ChangeState({ id, state, disabled }) {
  const { mutate, isLoading } = useClusterStateModification(
    state === 'on' ? 'off' : 'on'
  );
  const disabledForNonOnOffStates =
    disabled || isLoading || (state !== 'on' && state !== 'off');
  const nextDatabaseState = state === 'on' ? 'stop' : 'launch';
  const title = disabledForNonOnOffStates
    ? `Can't ${nextDatabaseState} server`
    : `${_.capitalize(nextDatabaseState)} server`;
  return (
    <Tooltip title={title}>
      <span>
        <IconButton
          data-cy={state === 'on' ? 'stop-cluster' : 'start-cluster'}
          sx={{ color: state === 'on' ? 'inherit' : 'success.main' }}
          onClick={() => {
            mutate(id);
          }}
          disabled={disabledForNonOnOffStates}
        >
          {state === 'on' ? <StopIcon /> : <PlayArrowIcon />}
        </IconButton>
      </span>
    </Tooltip>
  );
}

const defaultCacheTime = 5 * 60 * 1000; // 5 min
const smallerFontSize = '0.875rem';

const userPlaceholder = 'admin';
const passwordPlaceholder = 'password1234';

function SparqlEndpoint({ httpAddress }) {
  const url = new URL(httpAddress);
  url.pathname = '/repositories/actors/sparql';
  const urlString = url.toString();
  return (
    <>
      <ConnectionTitle
        title={
          <>
            You can provide anonymous endpoint access to your database or
            connect via an application that supports SPARQL Endpoint
            connections, such as Gruff. Note that Gruff requires a monthly
            password for SPARQL Endpoint connections. To obtain this password,
            please email us at{' '}
            <Link href="mailto:info@franz.com">info@franz.com</Link>
          </>
        }
      />
      <Typography fontWeight="bold">
        Example endpoint to "actors" repository:
      </Typography>{' '}
      <Typography>
        <Typography sx={{ wordBreak: 'break-all' }} component="span">
          {urlString}
        </Typography>{' '}
        <CopyToClipboard value={urlString} />
      </Typography>
      <Typography>
        <Typography fontWeight="bold" component="span">
          User:
        </Typography>{' '}
        {userPlaceholder}{' '}
        <Typography
          color="text.secondary"
          fontSize={smallerFontSize}
          component="span"
        >
          (If you created an anonymous user account then no User/Password is
          required)
        </Typography>
      </Typography>
      <Typography>
        <Typography fontWeight="bold" component="span">
          Password:
        </Typography>{' '}
        {passwordPlaceholder}{' '}
        <Typography
          color="text.secondary"
          fontSize={smallerFontSize}
          component="span"
        >
          (If you created an anonymous user account then no User/Password is
          required)
        </Typography>
      </Typography>
    </>
  );
}

const inputStylesFromGruff = {
  bgcolor: 'common.white',
  width: 350,
  height: 29,
  p: 0,
  pl: '5px',
  pr: '5px',
  '& input': {
    p: 0,
  },
};

function GruffLikeInputWithLabel({ label, value, inputSx, withCopy = false }) {
  return (
    <Stack>
      <Typography>{label}</Typography>
      <Stack direction="row" spacing={1} alignItems="center" height={40}>
        <OutlinedInput
          sx={{ ...inputStylesFromGruff, ...inputSx }}
          value={value}
        />
        {withCopy ? <CopyToClipboard value={value} /> : null}
      </Stack>
    </Stack>
  );
}

const smallerGruffInputSx = { width: 143 };

function ConnectionTitle({ titleSx, title, docLink }) {
  return (
    <Typography fontSize={smallerFontSize} pb={1} pt={1} sx={titleSx}>
      {title}.{' '}
      {docLink ? (
        <Link href={docLink} target="_blank">
          Learn more
        </Link>
      ) : null}
    </Typography>
  );
}

function GruffConnection({ httpAddress }) {
  const url = new URL(httpAddress);
  return (
    <>
      <ConnectionTitle
        title="Gruff is advanced no-code Graph Visualization, Graphical Query Builder, and Knowledge Graph Explorer"
        docLink="https://allegrograph.com/gruff-learning-center/"
      />
      <Typography fontWeight="bold">Gruff's connection UI:</Typography>
      <Stack p={1.5} spacing={1} bgcolor="rgb(240, 240, 240)">
        <GruffLikeInputWithLabel
          withCopy
          label="Server Machine"
          value={url.hostname}
        />
        <Stack direction="row" spacing={2} alignItems="center">
          <GruffLikeInputWithLabel
            withCopy
            label="Port Number"
            value={url.port || '443'}
            inputSx={smallerGruffInputSx}
          />
          <GruffLikeInputWithLabel
            withCopy
            label="Scheme"
            value={url.protocol.slice(0, -1)}
            inputSx={smallerGruffInputSx}
          />
        </Stack>
        <GruffLikeInputWithLabel label="User" value={userPlaceholder} />
        <GruffLikeInputWithLabel label="Password" value={passwordPlaceholder} />
      </Stack>
    </>
  );
}

const codeStyles = {
  backgroundColor: '#eee',
  borderRadius: '3px',
  fontFamily: 'courier, monospace',
  padding: '0 3px',
  overflow: 'auto',
};

function AgtoolConnection({ httpAddress }) {
  const url = new URL(httpAddress);
  url.username = userPlaceholder;
  url.password = passwordPlaceholder;
  const connectionSpec = url.toString().slice(0, -1);
  return (
    <>
      <ConnectionTitle
        title="agtool is CLI for AllegroGraph"
        docLink="https://franz.com/agraph/support/documentation/current/agtool.html"
      />
      <Typography fontWeight="bold">
        Server specification to connect via agtool:
      </Typography>
      <Typography component="span" sx={{ wordBreak: 'break-all' }}>
        {connectionSpec} <CopyToClipboard value={connectionSpec} />
      </Typography>
      <Typography fontWeight="bold">List repositories via agtool:</Typography>
      <code style={codeStyles}>
        {'>'} ./agtool repos list {connectionSpec}
        <pre>
          {`|             REPO | CATALOG | TYPE |
|------------------|---------|------|
|           actors |    root |    - |
|          chomsky |    root |    - |
|        contracts |    root |    - |
|           system |  system |    - |`}
        </pre>
      </code>
    </>
  );
}

function CurlConnection({ httpAddress }) {
  const url = new URL(httpAddress);
  url.pathname = '/repositories/actors/sparql';
  const cliExample = `curl -u ${userPlaceholder}:${passwordPlaceholder} \\
       --header 'Accept: application/json' \\
       -d 'query=select * { ?s ?p ?o . }' \\
       -d 'limit=10' \\
       ${url.toString()}`;
  return (
    <>
      <ConnectionTitle
        title="AllegroGraph allows SPARQL query execution via HTTP interface. For more details and other HTTP endpoints see AllegroGraph documentation"
        docLink="https://franz.com/agraph/support/documentation/current/http-reference.html"
      />
      <Typography noWrap fontWeight="bold">
        SPARQL query example via cURL with JSON output:{' '}
        <CopyToClipboard value={cliExample} />
      </Typography>
      <code style={codeStyles}>
        <pre>
          {'>'} {cliExample}
        </pre>
      </code>
    </>
  );
}

const connectionOptions = [
  { label: 'SPARQL Endpoint', Component: SparqlEndpoint },
  { label: 'Gruff', Component: GruffConnection },
  { label: 'agtool', Component: AgtoolConnection },
  { label: 'cURL', Component: CurlConnection },
];
const widthToFitAllTabs = 510;

function ConnectionInfo({ httpAddress }) {
  const [value, setValue] = React.useState(0);
  const ConnectionInfoComponent = connectionOptions[value].Component;
  return (
    <Stack p={2} pt={0} width="min-content" maxWidth={1}>
      <Tabs
        variant="scrollable"
        value={value}
        onChange={(e, newValue) => {
          setValue(newValue);
        }}
      >
        {connectionOptions.map(({ label }) => (
          <Tab
            disabled={!httpAddress}
            key={label}
            label={label}
            sx={{ textTransform: 'none', fontWeight: 'bold' }}
          />
        ))}
      </Tabs>
      {httpAddress ? (
        <Stack overflow="auto">
          <Stack width={widthToFitAllTabs}>
            <ConnectionInfoComponent httpAddress={httpAddress} />
          </Stack>
        </Stack>
      ) : (
        <Typography color="text.disabled">
          No information about server. Launch it to see connection options.
        </Typography>
      )}
    </Stack>
  );
}

function httpAddressFromUrl(url) {
  if (!url) {
    return url;
  }
  try {
    const { origin } = new URL(url);
    return `${origin}:443`;
  } catch {
    return '';
  }
}

export default function Instance({
  disabled,
  deleteCluster,
  ...initClusterInfo
}) {
  const { data, error, isError } = useQuery(
    ['cluster', initClusterInfo.id],
    ({ signal }) =>
      networkWithErrorConvert({ ...queryCluster(initClusterInfo.id), signal }),
    {
      retry: 3,
      enabled: !initClusterInfo.fake,
      refetchInterval: (clusterInfo, query) =>
        query?.state?.state === 'error'
          ? 0
          : clusterInfo?.state === 'changing'
          ? 500
          : defaultCacheTime,
    }
  );
  const clusterWithErrorInfo = {
    customTitle: error?.message,
    ...data,
    ...(isError ? { state: 'error' } : {}),
    ...initClusterInfo,
  };
  const httpAddress = httpAddressFromUrl(clusterWithErrorInfo?.url);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  return (
    <Paper
      data-cy="instance-container"
      sx={{
        height: 'max-content',
        width: '680px',
        display: 'flex',
        flexDirection: 'column',
        p: 2,
        textAlign: 'left',
        gap: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }} disableGutters>
        <Typography fontWeight="bold">
          Name:{' '}
          <Typography component="span">
            {clusterWithErrorInfo?.['display-name'] || ''}
          </Typography>
        </Typography>
        <Box
          sx={{
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <StateIcon {...clusterWithErrorInfo} />
          <ChangeState
            id={clusterWithErrorInfo.id}
            state={clusterWithErrorInfo.state}
            disabled={disabled}
          />
          <Options
            disabled={disabled}
            deleteCluster={deleteCluster}
            {...clusterWithErrorInfo}
          />
        </Box>
      </Box>
      <Typography fontWeight="bold" pb={1}>
        AllegroGraph WebView:{' '}
        {clusterWithErrorInfo?.url ? (
          <Link
            sx={{ wordBreak: 'break-all' }}
            href={clusterWithErrorInfo.url}
            target="_blank"
            variant="body1"
          >
            {clusterWithErrorInfo?.url}
          </Link>
        ) : (
          <Typography component="span">N/A</Typography>
        )}
      </Typography>
      <Button
        variant="contained"
        onClick={handleClick}
        sx={{ alignSelf: 'flex-end' }}
      >
        Connections <ArrowDropDownIcon />
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <ConnectionInfo httpAddress={httpAddress} />
      </Popover>
    </Paper>
  );
}
